import type { Right } from '@gozoki/tools'
import type { LogisticsTabValue } from '~/routes/_authenticated/logistics/-search-schema'
import type { PermissionTuple } from '~/utils/ability'
import type { PermissionRequirement } from '~/utils/rbac'

/**
 * Exigences de droits du module Logistique, en un seul endroit (mêmes ressources que les gardes de
 * l'API, section par section). Sans import d'icône, pour être testées sous Node ; `tabs.ts` les
 * réexporte avec les onglets de la page.
 */

const SIMULATION_READ: PermissionTuple = ['read', 'logistics.simulation.simulation']
const SIMULATION_ADD: PermissionTuple = ['add', 'logistics.simulation.simulation']
const SIMULATION_EDIT: PermissionTuple = ['edit', 'logistics.simulation.simulation']
const TOUR_READ: PermissionTuple = ['read', 'logistics.simulation.tour']
const TOUR_VALIDATE: PermissionTuple = ['validate-tour', 'logistics.simulation.tour']
const SIMULATION_LAUNCH: PermissionTuple = ['launch-simulation', 'logistics.simulation.simulation']
const HUB_READ: PermissionTuple = ['read', 'logistics.referentiels.hub']
const HUB_ADD: PermissionTuple = ['add', 'logistics.referentiels.hub']
const HUB_EDIT: PermissionTuple = ['edit', 'logistics.referentiels.hub']
const CARRIER_READ: PermissionTuple = ['read', 'logistics.referentiels.carrier']
const CARRIER_ADD: PermissionTuple = ['add', 'logistics.referentiels.carrier']
const CARRIER_EDIT: PermissionTuple = ['edit', 'logistics.referentiels.carrier']
const CARRIER_DELETE: PermissionTuple = ['delete', 'logistics.referentiels.carrier']
const VEHICLE_READ: PermissionTuple = ['read', 'logistics.referentiels.vehicle']
const VEHICLE_ADD: PermissionTuple = ['add', 'logistics.referentiels.vehicle']
const VEHICLE_EDIT: PermissionTuple = ['edit', 'logistics.referentiels.vehicle']
const DRIVER_READ: PermissionTuple = ['read', 'logistics.referentiels.driver']
const DRIVER_ADD: PermissionTuple = ['add', 'logistics.referentiels.driver']
const DRIVER_EDIT: PermissionTuple = ['edit', 'logistics.referentiels.driver']
const COLLABORATOR_READ: PermissionTuple = ['read', 'logistics.referentiels.collaborator']
const COLLABORATOR_EDIT: PermissionTuple = ['edit', 'logistics.referentiels.collaborator']
const HR_STAFF_READ: PermissionTuple = ['read', 'hr.staff.collaborator']

/** Liste du simulateur et route du détail (`$simulationId`), comme `GET /logistics/simulations`. */
export const LOGISTICS_SIMULATION_READ: PermissionRequirement = SIMULATION_READ

/**
 * Bouton « Ajouter » (modale de création, ouverte depuis la liste) : la modale crée la simulation
 * (`POST /logistics/simulations`, `add`) PUIS lance son calcul (`POST …/runs`,
 * `launch-simulation`) — les deux droits, en plus de la lecture de la liste qui la porte. Le camion,
 * requis (#901), se choisit dans `GET /logistics/vehicles` : sa lecture aussi. Pas celle des
 * transporteurs, qui ne font que filtrer la liste. Plus strict que l'API, qui n'exige que `add` au
 * `POST` (le camion y est contrôlé par son périmètre) : c'est le choix dans la liste qui l'impose.
 */
export const LOGISTICS_SIMULATION_ADD: PermissionRequirement = [
  SIMULATION_READ,
  SIMULATION_ADD,
  SIMULATION_LAUNCH,
  VEHICLE_READ,
]

/**
 * « Enregistrer » de l'onglet Contraintes : modifie la simulation (`PATCH`, `edit`) PUIS relance
 * son calcul (`launch-simulation`) — les deux droits.
 */
export const LOGISTICS_SIMULATION_EDIT_AND_LAUNCH: PermissionRequirement = [
  SIMULATION_EDIT,
  SIMULATION_LAUNCH,
]

/** Retenir un scénario (`POST /logistics/scenarios/:id/select`, `edit`), depuis les alternatives. */
export const LOGISTICS_SIMULATION_EDIT: PermissionRequirement = SIMULATION_EDIT

/**
 * « Basculer en opérationnel » (`POST /logistics/tours/:id/validate`) : valider, et lire les
 * tournées — sans ce droit, le détail les masque et le bouton ne saurait pas qu'elle est déjà
 * validée.
 */
export const LOGISTICS_TOUR_VALIDATE: PermissionRequirement = [TOUR_READ, TOUR_VALIDATE]

/** « Relancer le calcul sans modifier » (`POST …/runs`). */
export const LOGISTICS_SIMULATION_LAUNCH: PermissionRequirement = SIMULATION_LAUNCH

/** Section Transporteurs de l'onglet Camions, comme `GET /logistics/carriers`. */
export const LOGISTICS_CARRIER_READ: PermissionRequirement = CARRIER_READ

/** « Ajouter un transporteur » (`POST /logistics/carriers`), depuis la liste qui exige la lecture. */
export const LOGISTICS_CARRIER_ADD: PermissionRequirement = [CARRIER_READ, CARRIER_ADD]

/**
 * « Modifier » et « Restaurer » un transporteur (`PATCH /logistics/carriers/:id`), depuis la liste.
 */
export const LOGISTICS_CARRIER_EDIT: PermissionRequirement = [CARRIER_READ, CARRIER_EDIT]

/**
 * « Archiver » un transporteur (`DELETE /logistics/carriers/:id`) : l'action `delete` de la
 * ressource archive, elle ne supprime jamais.
 */
export const LOGISTICS_CARRIER_ARCHIVE: PermissionRequirement = [CARRIER_READ, CARRIER_DELETE]

/** Lecture des hubs (`GET /logistics/hubs`), dont l'onglet Camions affiche les noms. */
export const LOGISTICS_HUB_READ: PermissionRequirement = HUB_READ

/** « Ajouter un hub » (`POST /logistics/hubs`), depuis la liste qui exige la lecture. */
export const LOGISTICS_HUB_ADD: PermissionRequirement = [HUB_READ, HUB_ADD]

/** Interrupteur Actif / Inactif de la liste des hubs (`PATCH /logistics/hubs/:id`), qui exige la lecture. */
export const LOGISTICS_HUB_EDIT: PermissionRequirement = [HUB_READ, HUB_EDIT]

/**
 * Fiche d'un camion (route `$vehicleId`), comme `GET /logistics/vehicles/:id`. Ses listes de choix
 * viennent d'autres référentiels, sous leurs propres droits : transporteurs (`carrier` read), hubs
 * (`hub` read), ateliers (configurateur industriel : flag et droit prévisions). Sans eux la fiche
 * reste utilisable : transporteurs et hubs non lus (`useVehicleReferences`), ateliers en erreur sous
 * leur sélecteur, valeurs aux libellés de repli de `logistics-places.ts`.
 */
export const LOGISTICS_VEHICLE_READ: PermissionRequirement = VEHICLE_READ

/**
 * « Ajouter un camion » et route `new` : la création (`POST /logistics/vehicles`, `add`) mène
 * ensuite à la fiche du camion créé, qui exige la lecture.
 */
export const LOGISTICS_VEHICLE_ADD: PermissionRequirement = [VEHICLE_READ, VEHICLE_ADD]

/** « Enregistrer » sur la fiche d'un camion existant (`PATCH /logistics/vehicles/:id`). */
export const LOGISTICS_VEHICLE_EDIT: PermissionRequirement = VEHICLE_EDIT

/**
 * Fiche d'un collaborateur logistique (route `$collaboratorId`), comme
 * `GET /logistics/collaborators/:id`. Ses champs RH restent soumis aux droits RH de l'admin sur ce
 * collaborateur (`capabilities` de la réponse) ; ses sélecteurs d'atelier et de hub, aux droits de
 * ces référentiels (cf. `LOGISTICS_VEHICLE_READ`).
 */
export const LOGISTICS_COLLABORATOR_READ: PermissionRequirement = COLLABORATOR_READ

/**
 * Lien vers la fiche collaborateur depuis la fiche hub (personnel rattaché) : la fiche, et la liste
 * RH qui dit quelles fiches le lecteur lit (`GET /hr/collaborators`, droit RH en plus du droit
 * logistique ; sans lui, 403 et toast « Droits insuffisants » à chaque fiche hub).
 */
export const LOGISTICS_COLLABORATOR_RECORD_LINK: PermissionRequirement = [
  COLLABORATOR_READ,
  HR_STAFF_READ,
]

/** « Enregistrer » sur la fiche d'un collaborateur (`PATCH /logistics/collaborators/:id`). */
export const LOGISTICS_COLLABORATOR_EDIT: PermissionRequirement = COLLABORATOR_EDIT

/**
 * Profils chauffeur : section de l'onglet RH (`GET /logistics/drivers`) et bloc « Complément rôle
 * Chauffeur » de la fiche collaborateur, que l'API ne rend qu'avec ce droit.
 */
export const LOGISTICS_DRIVER_READ: PermissionRequirement = DRIVER_READ

/**
 * Bloc « Complément rôle Chauffeur » d'un collaborateur sans profil : « Enregistrer » le crée
 * (`PATCH /logistics/collaborators/:id`, bloc `driver`). Aussi le dépôt d'une pièce
 * (`POST /logistics/drivers/:id/licences`).
 */
export const LOGISTICS_DRIVER_ADD: PermissionRequirement = DRIVER_ADD

/** Le même bloc quand le profil existe : « Enregistrer » le modifie. */
export const LOGISTICS_DRIVER_EDIT: PermissionRequirement = DRIVER_EDIT

/** Droits de lecture de chaque onglet ; « Gestion opérationnelle » n'existe pas encore. */
export const LOGISTICS_TAB_PERMISSIONS = {
  simulator: SIMULATION_READ,
  hubs: HUB_READ,
  // Transporteurs et camions : l'onglet porte les deux sections.
  vehicles: { any: [CARRIER_READ, VEHICLE_READ] },
  // Profils chauffeur (liste) ou fiches collaborateur : l'onglet mène aux deux.
  hr: { any: [DRIVER_READ, COLLABORATOR_READ] },
} as const satisfies Record<Exclude<LogisticsTabValue, 'operations'>, PermissionRequirement>

/**
 * Page Logistique : elle porte les cinq sections du module en un seul écran, son exigence est donc
 * l'union des lectures de ses onglets — un profil cantonné aux référentiels garde l'entrée de menu
 * et la page, les onglets qui lui sont interdits restent désactivés.
 */
export const LOGISTICS_READ: PermissionRequirement = {
  any: Object.values(LOGISTICS_TAB_PERMISSIONS),
}

/** Droits v1 exigés tant que le flag `permission-v2` est OFF : le bit historique du module. */
export const LOGISTICS_LEGACY_RIGHTS: Right[] = ['LOGISTICS']
